import { useState, type ReactElement } from 'react'
import { isDefined, isNotDefined, type NotificationItem } from '@terros/common'
import { showNotification } from '@mantine/notifications'
import { ActionIcon, Menu, Text } from '@mantine/core'
import {
  useArchiveNotification,
  useMarkNotificationRead,
  useNotificationItemUpdating,
  useNotificationSettings,
  useUpdateNotificationSettings,
} from '@front/model'
import { MaterialSymbolIcon } from '../components/terros/icon/MaterialSymbolIcon'

export function NotificationActions({ item }: { item: NotificationItem }): ReactElement {
  const [opened, setOpened] = useState(false)
  const mute = useUpdateNotificationSettings()
  const source = item.muteSource
  const canMute = isDefined(source) && !item.expired && !item.display.redacted
  const settings = useNotificationSettings({ enabled: canMute && opened })
  const muted = settings.data?.mutedSources.some((entry) => entry.kind === source?.kind && entry.id === source.id)
  const settingsReady =
    isDefined(settings.data) && !settings.isFetching && !settings.unavailable && isNotDefined(settings.error)
  const itemUpdating = useNotificationItemUpdating(item.id)
  const read = useMarkNotificationRead()
  const archive = useArchiveNotification()
  const busy = itemUpdating || read.isUpdating || archive.isUpdating || mute.isUpdating
  const unread = isNotDefined(item.state.readAt)
  const archived = isDefined(item.state.archivedAt)
  async function toggleRead(): Promise<void> {
    try {
      await read.update({ ids: [item.id], read: unread })
    } catch {
      showNotification({ color: 'red', message: 'Could not update notification. Please try again.' })
    }
  }
  async function toggleArchive(): Promise<void> {
    try {
      await archive.update({ ids: [item.id], archived: !archived })
    } catch {
      showNotification({ color: 'red', message: 'Could not update notification. Please try again.' })
    }
  }
  async function muteItem(): Promise<void> {
    if (!canMute || !settingsReady || isNotDefined(source)) return
    try {
      await mute.update({ mute: { sources: [source], muted: !muted } })
      showNotification({
        message: muted ? 'Item unmuted.' : 'Item muted. Manage muted items in notification settings.',
      })
    } catch {
      showNotification({ color: 'red', message: 'Could not update notification. Please try again.' })
    }
  }
  return (
    <>
      <Menu position='bottom-end' withinPortal={false} returnFocus opened={opened} onChange={setOpened}>
        <Menu.Target>
          <ActionIcon variant='subtle' aria-label={`Actions for ${item.display.title}`}>
            <MaterialSymbolIcon icon='more_vert' />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item disabled={busy} onClick={toggleRead}>
            {unread ? 'Mark read' : 'Mark unread'}
          </Menu.Item>
          <Menu.Item disabled={busy} onClick={toggleArchive}>
            {archived ? 'Restore' : 'Archive'}
          </Menu.Item>
          {canMute && settingsReady && (
            <Menu.Item disabled={busy} onClick={muteItem}>
              {muted ? 'Unmute item' : 'Mute item'}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
      {isDefined(read.error ?? archive.error ?? mute.error) && (
        <Text role='alert' size='sm' c='red'>
          {read.error ?? archive.error ?? mute.error} Try again using the row menu.
        </Text>
      )}
    </>
  )
}
